import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    ApplicationDetail,
    BlobType,
    EditPublishedApplicationPayload,
    FileModel,
    MLMeasurement,
    MlModelWithSegmentationSettings,
    Selected,
} from '@ca/core/models';
import { FileUploadService, UserService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Guid } from 'guid-typescript';
import { FileUploader } from 'ng2-file-upload';
import { SUPPORTED_IMAGE_TYPES } from 'src/app/shared/constants/form-validators';

@Component({
    selector: 'ca-edit-application-dialog',
    templateUrl: './edit-application-dialog.component.html',
    styleUrls: ['./edit-application-dialog.component.scss'],
})
export class EditApplicationDialogComponent implements AfterViewInit, OnInit {
    @Input() pathToAppend: string;
    @Input() set application(application: ApplicationDetail) {
        if (application) {
            this._application = application;
            if (this.formGroup) {
                this.formGroup.get('applicationName')?.setValue(application.name);
                this.formGroup.get('summary')?.setValue(application.summary);
                this.formGroup.get('description')?.setValue(application.description);
                this.formGroup.get('requirements')?.setValue(application.requirements);
                this.formGroup.get('thumbUrl')?.setValue(application.thumbUrl);
                this.imageUrls.clear();
                application.images.forEach((imageUrl: string) => {
                    this.imageUrls.push(new UntypedFormControl(imageUrl));
                });
            }
        }
    }
    get application(): ApplicationDetail {
        return this._application;
    }
    @Input() models: MlModelWithSegmentationSettings[];
    @Input() set measurements(measurements: MLMeasurement[]) {
        this.selectedMeasurements = this.application?.measurementIds
            ? measurements.filter(m => this.application.measurementIds!.findIndex(mid => mid === m.id) > -1)
            : [];
        this.getAllMeasurementsWithSelectedState(measurements, this.selectedMeasurements);
    }
    @Input() requestMeasurement: Function;
    @Output() onSubmit = new EventEmitter<EditPublishedApplicationPayload>();

    readonly uploaderKey = `image-uploader_${Guid.create().toString()}`;
    readonly uploaderThumbKey = `image-thumb-uploader_${Guid.create().toString()}`;
    readonly blobType: BlobType = BlobType.ModuleImage;

    uploader: FileUploader | null;
    uploaderThumb: FileUploader | null;
    image: FileModel | null = null;
    maxFileSizeInBytes: number = 10 * 1024 * 1024;
    supportedImageTypes: string[] = SUPPORTED_IMAGE_TYPES;
    multiple: boolean = true;
    isThumbUploading: boolean = false;
    isUploading: boolean = false;
    formGroup: UntypedFormGroup;
    displayMeasurements: Selected<MLMeasurement>[] = [];
    selectedMeasurements: MLMeasurement[] = [];

    private _application: ApplicationDetail;

    get imageUrls(): UntypedFormArray {
        return this.formGroup.get('imageUrls') as UntypedFormArray;
    }

    constructor(
        private activeModal: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private fileUploadService: FileUploadService,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group({
            applicationName: [this.application ? this.application.name : '', [Validators.required]],
            summary: [this.application ? this.application.summary : '', [Validators.required]],
            description: [this.application ? this.application.description : '', [Validators.required]],
            requirements: [this.application ? this.application.requirements : '', [Validators.required]],
            thumbUrl: [this.application ? this.application.thumbUrl : '', [Validators.required]],
            imageUrls: this.formBuilder.array(this.application ? this.application.images : [], Validators.required),
            selectedModel: null,
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.uploaderThumb = this.fileUploadService.fetchFromCache(this.uploaderThumbKey!);
            this.uploader = this.fileUploadService.fetchFromCache(this.uploaderKey!);
        });
    }

    dismiss(): void {
        this.activeModal.close();
    }

    onUploadStarted(): void {
        this.isUploading = true;
    }

    onThumbUploadStarted(): void {
        this.isThumbUploading = true;
    }

    onUploadCompleted(file: FileModel): void {
        const imageUrl = `${file.token}?refresh=${Guid.create()}`;
        const formControl: UntypedFormControl = new UntypedFormControl(imageUrl, Validators.required);
        this.imageUrls.push(formControl);
        this.isUploading = false;
    }

    onThumbUploadCompleted(file: FileModel): void {
        const thumbUrl = `${file.token}?refresh=${Guid.create()}`;
        this.formGroup.get('thumbUrl')?.setValue(thumbUrl);
        this.isThumbUploading = false;
    }

    removeFileItem(fileItem, index: number): void {
        if (fileItem) {
            if (fileItem.remove) {
                fileItem.remove();
            } else {
                fileItem.parentNode.removeChild(fileItem);
            }
            this.imageUrls.removeAt(index);
        }
    }

    removeThumbFileItem(): void {
        this.formGroup.get('thumbUrl')?.setValue('');
    }

    submit(): void {
        this.onSubmit.next({
            applicationName: this.formGroup.get('applicationName')?.value,
            summary: this.formGroup.get('summary')?.value,
            description: this.formGroup.get('description')?.value,
            requirements: this.formGroup.get('requirements')?.value,
            thumbUrl: this.formGroup.get('thumbUrl')?.value,
            imageUrls: this.imageUrls.value,
            measurementIds: this.selectedMeasurements.map(sm => sm.id),
            trainingId:
                this.formGroup.get('selectedModel') && this.formGroup.get('selectedModel')!.value
                    ? (this.formGroup.get('selectedModel')?.value as MlModelWithSegmentationSettings).resultId
                    : undefined,
        } as EditPublishedApplicationPayload);
    }

    isUserOwner(ownerId: string): boolean {
        return this.userService.isCurrentUser(ownerId);
    }

    toggleMeasurement(measurement: Selected<MLMeasurement>): void {
        measurement.selected = !measurement.selected;
        this.selectedMeasurements = this.displayMeasurements.filter(dm => dm.selected).map(dm => dm.child);
    }

    requestMlMeasurement(): void {
        this.requestMeasurement();
    }

    private getAllMeasurementsWithSelectedState(allMeasurements: MLMeasurement[], selectedMeasurements: MLMeasurement[]): Selected<MLMeasurement>[] {
        return allMeasurements.map(m => {
            const isSelected = selectedMeasurements.findIndex(sm => sm.id === m.id) > -1;
            return { title: m.name, child: m, selected: isSelected };
        });
    }
}
